import { Link } from 'react-router-dom';
import { HELP_COPY, ROLES } from '@saesak/shared';
import { AppBar, Screen } from '@/components/layout';
import { Card, SectionTitle } from '@/components/ui';
import { useAuth } from '@/store/auth';

/**
 * 도움말 (v1.18) — 우선 목업. 자주 묻는 질문을 역할에 맞춰 보여 준다.
 * 내용이 쌓이면 검색·카테고리로 키운다.
 */
export function HelpPage() {
  const role = useAuth((s) => s.membership?.role);
  const isParent = role === ROLES.PARENT;

  const sections = isParent ? PARENT_FAQ : STAFF_FAQ;

  return (
    <Screen>
      <AppBar title={HELP_COPY.title} back />

      <div className="p-4">
        <p className="text-base leading-relaxed text-ink-sub">{HELP_COPY.intro}</p>

        {sections.map((section) => (
          <div key={section.title}>
            <SectionTitle>{section.title}</SectionTitle>
            <div className="flex flex-col gap-3">
              {section.items.map((item) => (
                <Card key={item.q}>
                  <p className="font-semibold">{item.q}</p>
                  <p className="mt-1.5 text-base leading-relaxed text-ink-sub">{item.a}</p>
                </Card>
              ))}
            </div>
          </div>
        ))}

        <p className="mt-6 text-center text-sm text-ink-sub">
          더 궁금한 점이 있다면{' '}
          <Link to="/settings/feedback" className="font-semibold text-primary-strong underline">
            불편·제안 보내기
          </Link>
          로 들려주세요.
        </p>
      </div>
    </Screen>
  );
}

interface FaqSection {
  title: string;
  items: { q: string; a: string }[];
}

const PARENT_FAQ: FaqSection[] = [
  {
    title: '시작하기',
    items: [
      {
        q: '초대코드는 어디서 받나요?',
        a: '아이가 다니는 원에서 보호자 초대코드를 보내 드려요. 코드를 입력하면 가입부터 아이 연결까지 한 번에 끝나요.',
      },
      {
        q: '앱 알림이 오지 않아요.',
        a: '전체 메뉴의 알림 화면에서 알림 받기가 켜져 있는지, 휴대폰 설정에서 새싹돋보기 알림이 허용되어 있는지 확인해 주세요.',
      },
    ],
  },
  {
    title: '아이의 하루',
    items: [
      {
        q: '알림장에 답장할 수 있나요?',
        a: '알림장 아래 댓글과 이모지로 마음을 전할 수 있어요. 등원 전 전달사항은 "가정에서 전하기"로 보내 주세요.',
      },
      {
        q: '사진 원본 저장이 안 돼요.',
        a: '아이들의 초상권 보호를 위해 원본 저장은 원의 방침에 따라 잠글 수 있어요. 원에 문의해 주세요.',
      },
      {
        q: '불편한 쪽지나 댓글을 받았어요.',
        a: '쪽지·댓글 옆 ⋯ 메뉴에서 신고하거나 상대를 차단할 수 있어요. 신고는 원장님과 운영진이 확인하고, 차단한 사람의 쪽지·댓글은 내 화면에서 보이지 않아요. 앱 설정 → 차단한 사용자에서 언제든 해제할 수 있어요.',
      },
      {
        q: '버스가 지금 어디쯤인지 알 수 있나요?',
        a: '버스 메뉴에서 운행 중인 버스의 위치와 "우리 아이 정거장까지 몇 정거장 전인지"를 볼 수 있고, 가까워지면 알림이 와요.',
      },
    ],
  },
  {
    title: '성장 기록',
    items: [
      {
        q: '돋보기의 새싹 나무는 무엇인가요?',
        a: '아이의 발달을 씨앗부터 열매까지 일곱 단계로 보여 주는 화면이에요. 또래와 비교하지 않고 아이 자신의 속도만 봐요.',
      },
      {
        q: '생활기록부는 어떻게 받나요?',
        a: '원에서 발급하면 생활기록부 메뉴에서 PDF로 바로 받아 간직할 수 있어요.',
      },
    ],
  },
];

const STAFF_FAQ: FaqSection[] = [
  {
    title: '기록하기',
    items: [
      {
        q: '알림장을 빠르게 쓰는 방법이 있나요?',
        a: '컨디션 버튼 몇 번이면 기본 틀이 완성돼요. 반 전체에 한 번에 보내고 아이별 컨디션만 따로 남길 수도 있어요.',
      },
      {
        q: 'AI 초안은 어떻게 만들어지나요?',
        a: '선생님이 남긴 관찰 메모만 근거로 삼아요. 관찰하지 않은 이야기는 지어내지 않고, 저장 전에 반드시 선생님이 확인해요.',
      },
    ],
  },
  {
    title: '운영하기',
    items: [
      {
        q: '서류는 어디에 모이나요?',
        a: '앱에서 만들어진 투약의뢰서·귀가동의서·생활기록부는 아이별 서류철에 자동으로 편철돼요. 전자 서류함에서 한 번에 봐요.',
      },
      {
        q: '지도점검 준비는 어떻게 하나요?',
        a: '전자 서류함의 지도점검 묶음이 내야 하는 서류만 자동으로 골라 줘요. 평소엔 출력할 일이 없어요.',
      },
      {
        q: '신고가 들어오면 어떻게 하나요?',
        a: '앱 관리 화면의 "신고 접수"에서 내용을 확인하고 숨기거나 당사자에게 안내한 뒤 닫아 주세요. 욕설이나 주민등록번호·카드번호가 담긴 쪽지·댓글은 보내기 전에 자동으로 걸러져요.',
      },
      {
        q: '구매대장은 어떻게 쓰나요?',
        a: '영수증·견적서·주문 캡처를 사진이나 PDF로 올리면 AI가 항목을 읽어 초안을 채워요. 확인하고 저장하면 대장에 한 줄이 쌓이고, 월별 PDF는 원장님이 정한 양식으로 나와요.',
      },
    ],
  },
];
